import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Download, List, Minus, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { StatCard } from '@ooh/ui/components/stat-card';
import { BULK_PHOTO_IMPORT_JOB_TYPE, importEndpoints } from '@ooh/contracts';
import type { ImportJob } from '@ooh/contracts';
import { Link } from '../../plugs/link';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryOptions, useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { safeCount, computeImportedRows } from '../lib/import-stats';
import { triggerErrorReportDownload } from '../../lib/error-report-csv';
import { resolveValidationMsg, type TFn } from './validation-tables';
import { PhotoBundleResultPhase } from './photo-bundle-result-phase';

interface ResultPhaseProps {
  importJobId: number;
  /** Called when user clicks "Powrót do importu" (photo-bundle flow only). */
  onBackToImport?: () => void;
}

/** Exported for unit testing. */
export function computeResultStats(job: ImportJob | undefined) {
  const processedRows = safeCount(job?.processedRows);
  const errorRows = safeCount(job?.errorRows);
  const updatedRows = safeCount(job?.updatedRows);
  const imported = computeImportedRows(processedRows, updatedRows);
  const totalRows = safeCount(job?.totalRows);
  const isFailed = job?.status === 'failed';
  return { imported, updatedRows, errorRows, totalRows, isFailed };
}

interface XlsxResultPhaseProps {
  importJobId: number;
  job: ImportJob;
}

function XlsxResultPhase({ importJobId, job }: XlsxResultPhaseProps) {
  const { t } = useTranslation('mediaImport');
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { imported, updatedRows, errorRows, totalRows, isFailed } = computeResultStats(job);

  const handleDownloadErrorReport = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await queryClient.fetchQuery(
        apiQueryOptions(api, importEndpoints.getErrors, { id: importJobId }),
      );
      triggerErrorReportDownload(
        importJobId,
        response.errors,
        {
          row: t('result.errorReportColumnRow'),
          grwId: t('result.errorReportColumnGrwId'),
          field: t('result.errorReportColumnField'),
          message: t('result.errorReportColumnMessage'),
          severity: t('result.errorReportColumnSeverity'),
        },
        // Cast matches validation-tables.tsx's own call sites: i18next's `t` type expects
        // statically-known key literals, but the validation error `code` is server-driven.
        (code, message, params) => resolveValidationMsg(t as unknown as TFn, code, message, params),
      );
    } catch (error) {
      console.error('Failed to download import error report', { importJobId, error });
      toast.error(t('result.downloadErrorReportFailed'));
    } finally {
      setIsDownloading(false);
    }
  }, [queryClient, api, importJobId, t]);
  const headingBg = isFailed ? 'bg-red-100' : 'bg-emerald-100';
  const checkColor = isFailed ? 'text-red-600' : 'text-emerald-600';
  const heading = isFailed ? t('result.headingWithErrors') : t('result.headingSuccess');

  return (
    <div className="max-w-xl mx-auto space-y-6 py-6" data-testid="phase-result">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className={`flex h-16 w-16 items-center justify-center rounded-full ${headingBg}`}>
          <Check className={`h-8 w-8 ${checkColor}`} />
        </div>
        <div>
          <p className="text-[17px] font-semibold">{heading}</p>
          <p className="text-[13px] text-muted-foreground mt-1">
            {t('result.processed', { totalRows })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="result-stats">
        <StatCard
          icon={Check}
          label={t('result.statImported')}
          value={imported}
          loading={false}
          tone="success"
          testId="stat-imported"
        />
        <StatCard
          icon={RefreshCw}
          label={t('result.statUpdated')}
          value={updatedRows}
          loading={false}
          testId="stat-updated"
        />
        <StatCard
          icon={Minus}
          label={t('result.statSkipped')}
          value={0}
          loading={false}
          testId="stat-skipped"
        />
        <StatCard
          icon={XCircle}
          label={t('result.statErrors')}
          value={errorRows}
          loading={false}
          tone="destructive"
          testId="stat-errors"
        />
      </div>

      <div className="flex flex-col gap-3">
        {job.id != null ? (
          <Link to={`/media-units?importJobId=${job.id}`} data-testid="view-imported-items">
            <Button type="button" fullWidth>
              <List className="h-4 w-4" />
              {t('result.viewImportedButton')}
            </Button>
          </Link>
        ) : (
          <Button type="button" fullWidth disabled data-testid="view-imported-items">
            <List className="h-4 w-4" />
            {t('result.viewImportedButton')}
          </Button>
        )}
        {/* CU-86caecmcr: also show the button for a failed job with errorRows still at 0
            (e.g. a hard guard like the row-limit check that aborts before any row is
            counted) — the errorLog itself may still hold the one entry that explains
            what happened, and hiding the only way to read it left users staring at an
            unexplained "0 imported / 0 updated / 0 errors" result. */}
        {Boolean(errorRows > 0 || isFailed) && (
          <Button
            variant="outline"
            type="button"
            fullWidth
            data-testid="download-error-report"
            disabled={isDownloading}
            onClick={() => {
              void handleDownloadErrorReport();
            }}
          >
            <Download className="h-4 w-4" />
            {t('result.downloadErrorReport', { count: errorRows })}
          </Button>
        )}
        <Link to="/media-units" data-testid="go-to-inventory">
          <Button variant="ghost" type="button" fullWidth>
            {t('result.goToInventory')}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function ResultPhase(props: ResultPhaseProps) {
  return (
    <QueryBoundary fallback={<SectionSkeleton />}>
      <LoadedResultPhase {...props} />
    </QueryBoundary>
  );
}

function LoadedResultPhase({ importJobId, onBackToImport }: ResultPhaseProps) {
  const { data: job } = useApiSuspenseQuery(
    importEndpoints.getStatus,
    { id: importJobId },
    { staleTime: Infinity },
  );

  if (job.type === BULK_PHOTO_IMPORT_JOB_TYPE) {
    return (
      <PhotoBundleResultPhase
        importJobId={importJobId}
        jobStatus={job.status}
        resultSummary={job.resultSummary}
        onBackToImport={onBackToImport}
      />
    );
  }

  return <XlsxResultPhase importJobId={importJobId} job={job} />;
}
